// ============================================================================
// Delhivery Bookings — dashboard list over GET /delhivery-bookings/. Pure DB, fast/pollable:
// filter by PO, order id, booked-date range, and fulfilment status; paginate server-side.
// Deleted/revised rows (e.g. bookings hijacked by the legacy Porter conversion) stay visible,
// flagged with tags. Drill into any row's live tracking via the single-PO "Delhivery Booking" tab.
// ============================================================================
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Collapse,
  DatePicker,
  Descriptions,
  Dropdown,
  Drawer,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { DownOutlined, EnvironmentOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";
import dayjs from "dayjs";
import React from "react";
import {
  DELHIVERY_CANCEL_REASONS,
  DelhiveryBookingRow,
  DelhiveryBookingsFilters,
  DelhiveryReconcileResult,
  delhiveryCancelOrder,
  delhiveryMultistopCancel,
  delhiveryMultistopDetach,
  delhiveryReconcileActive,
  getDelhiveryBookings,
} from "../../apis/po.api";
import { googleMapsUrl } from "../oms/format";
import { deliveryTeamName } from "../oms/logistics/contact_select";

// A contact phone for the Additional-details drawer: "Name - number" when the number is a known
// Delivery Team member, else just the number. Empty → em dash.
const contactDisplay = (value: string | null | undefined): string => {
  if (!value) return "—";
  const name = deliveryTeamName(value);
  return name ? `${name} - ${value}` : value;
};

const { Title } = Typography;
const { RangePicker } = DatePicker;

const PAGE_SIZE = 50;

// The 3 tabs are just status presets on the same list. Active = in-flight fulfilment statuses (sent
// as a repeated status= list); Delivered = the terminal delivered state; All = no status filter.
type BookingsTab = "active" | "delivered" | "all";
const ACTIVE_STATUSES = [
  "searching_for_agent",
  "agent_assigned",
  "at_pickup",
  "out_for_delivery",
  "at_delivery",
];
const DELIVERED_STATUSES = ["order_delivered"];
const statusForTab = (t: BookingsTab): string[] | undefined =>
  t === "active" ? ACTIVE_STATUSES : t === "delivered" ? DELIVERED_STATUSES : undefined;

// Full status list for the manual dropdown shown only on the All tab.
const STATUS_OPTIONS = [
  "pending",
  "searching_for_agent",
  "agent_assigned",
  "at_pickup",
  "out_for_delivery",
  "at_delivery",
  "order_delivered",
  "cancelled",
];

// Colour the fulfilment status so the terminal/failure states read at a glance.
const statusColor = (s?: string | null) => {
  if (!s) return undefined;
  if (s === "order_delivered" || s === "delivered") return "green";
  if (s === "cancelled") return "red";
  if (s === "out_for_delivery" || s === "at_delivery") return "blue";
  if (s === "agent_assigned" || s === "at_pickup") return "cyan";
  return "default";
};

// One endpoint of the route — the map-pin + address label, truncated to one line (full text on hover),
// as a clickable Google Maps link that pins the precise lat/long when present. Mirrors the logistics
// board's locationCell.
const AddressLink: React.FC<{
  label: string | null;
  lat: number | null;
  long: number | null;
}> = ({ label, lat, long }) => {
  if (!label) return <span style={{ color: "#bbb" }}>—</span>;
  const url = googleMapsUrl({ lat, long }, [label]);
  const line = (
    <span
      style={{
        display: "block",
        whiteSpace: "normal",
        wordBreak: "break-word",
      }}
    >
      <EnvironmentOutlined style={{ color: "#1677ff", marginRight: 4 }} />
      {label}
    </span>
  );
  const el = url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "inherit", display: "block" }}
    >
      {line}
    </a>
  ) : (
    line
  );
  return <Tooltip title={label}>{el}</Tooltip>;
};

// The combined From → To route cell: origin on top, a down arrow, then destination — each clickable.
const RouteCell: React.FC<{ row: DelhiveryBookingRow }> = ({ row }) => {
  if (!row.origin_label && !row.destination_label)
    return <span style={{ color: "#bbb" }}>—</span>;
  return (
    <div style={{ fontSize: 12, lineHeight: 1.4, width: "100%", textAlign: "center" }}>
      <AddressLink
        label={row.origin_label}
        lat={row.origin_lat}
        long={row.origin_long}
      />
      <div
        style={{
          display: "block",
          width: "100%",
          textAlign: "center",
          color: "#8c8c8c",
        }}
      >
        ↓
      </div>
      <AddressLink
        label={row.destination_label}
        lat={row.destination_lat}
        long={row.destination_long}
      />
    </div>
  );
};

// The booking's line items, in a fixed-height cell that scrolls vertically when there are many. The
// greyed cargo weight / max length sit above the list and stay visible while the items scroll.
const CargoItemsCell: React.FC<{
  items?: DelhiveryBookingRow["cargo_items"];
  weightKg?: number | null;
  maxLengthFt?: number | null;
}> = ({ items, weightKg, maxLengthFt }) => {
  const summary = [
    typeof weightKg === "number" && weightKg > 0
      ? `Total weight: ${weightKg} kg`
      : null,
    typeof maxLengthFt === "number" && maxLengthFt > 0 ? `${maxLengthFt} ft` : null,
  ].filter(Boolean);
  const hasItems = Array.isArray(items) && items.length > 0;
  if (!hasItems && summary.length === 0)
    return <span style={{ color: "#bbb" }}>—</span>;
  return (
    <div>
      {summary.length > 0 && (
        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
          {summary.join(" · ")}
        </div>
      )}
      {hasItems && (
        <div style={{ maxHeight: 110, overflowY: "auto", paddingRight: 4 }}>
          {items!.map((it) => (
            <div
              key={it.allocation_item_id}
              style={{ fontSize: 12, lineHeight: 1.3, marginBottom: 6 }}
            >
              <div>{it.name}</div>
              <div style={{ color: "#8c8c8c" }}>
                {it.quantity} {it.unit || ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Row timestamps come through as ISO strings or epoch ms — format either, or "—".
const fmtTime = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === "") return "—";
  const d = typeof v === "number" ? dayjs(v) : dayjs(v);
  return d.isValid() ? d.format("DD MMM YYYY, HH:mm") : String(v);
};

const DelhiveryBookings: React.FC = () => {
  const [rows, setRows] = React.useState<DelhiveryBookingRow[]>([]);
  const [count, setCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);

  // Which tab is active — drives the status filter.
  const [tab, setTab] = React.useState<BookingsTab>("active");

  // The row whose "Additional details" drawer is open (null = closed).
  const [detailsRow, setDetailsRow] = React.useState<DelhiveryBookingRow | null>(null);

  // Cancel-booking prompt: the PO to cancel, the required reason, and the in-flight flag.
  const [cancelPo, setCancelPo] = React.useState<string | null>(null);
  const [cancelReason, setCancelReason] = React.useState("");
  const [cancelling, setCancelling] = React.useState(false);

  // Filter inputs (draft — applied on Search / pagination).
  const [poNumber, setPoNumber] = React.useState("");
  // Manual status filter — only used (and shown) on the All tab.
  const [status, setStatus] = React.useState<string | undefined>();
  const [bookedRange, setBookedRange] = React.useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null);
  const [orderId, setOrderId] = React.useState("");

  const load = React.useCallback(
    async (toPage: number) => {
      setLoading(true);
      const filters: DelhiveryBookingsFilters = {
        po_number: poNumber.trim() || undefined,
        order_id: orderId.trim() || undefined,
        booked_from: bookedRange?.[0]?.format("YYYY-MM-DD"),
        booked_to: bookedRange?.[1]?.format("YYYY-MM-DD"),
        // Active/Delivered are fixed presets; All uses the manual status dropdown (or none).
        status: tab === "all" ? status || undefined : statusForTab(tab),
        limit: PAGE_SIZE,
        offset: (toPage - 1) * PAGE_SIZE,
      };
      const res = await getDelhiveryBookings(filters);
      if (res) {
        setRows(res.results ?? []);
        setCount(res.count ?? 0);
        setPage(toPage);
      }
      setLoading(false);
    },
    [poNumber, orderId, bookedRange, tab, status]
  );

  // Load on mount and whenever the tab changes (each tab is a different status preset).
  React.useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // "Sync delivery statuses" — Tracks every active booking against Delhivery and catches stuck
  // rows up. Slow; the loading guard both shows a spinner and blocks concurrent/double runs.
  const [syncing, setSyncing] = React.useState(false);
  const [syncResult, setSyncResult] = React.useState<DelhiveryReconcileResult | null>(null);

  const syncSummary = (d: DelhiveryReconcileResult) =>
    `Synced ${d.scanned} bookings — ${d.reconciled_delivered.length} marked delivered, ` +
    `${d.reconciled_cancelled.length} cancelled, ${d.advanced.length} updated, ` +
    `${d.unchanged} already up to date, ${d.errors.length} errors.`;

  const onSync = async () => {
    if (syncing) return;
    setSyncing(true);
    const res = await delhiveryReconcileActive(500);
    setSyncing(false);
    if (res.ok && res.data) {
      setSyncResult(res.data);
      message.success(syncSummary(res.data));
      load(page); // refresh so the newly-updated statuses show
    } else {
      message.error("Sync failed, please retry");
    }
  };

  const onSearch = () => load(1);

  const onReset = () => {
    setPoNumber("");
    setOrderId("");
    setBookedRange(null);
    setStatus(undefined);
    // Reload with just the current tab's status filter, on the next tick after state clears.
    setTimeout(() => {
      setPage(1);
      getDelhiveryBookings({
        status: statusForTab(tab),
        limit: PAGE_SIZE,
        offset: 0,
      }).then((res) => {
        if (res) {
          setRows(res.results ?? []);
          setCount(res.count ?? 0);
        }
      });
    }, 0);
  };

  const runCancel = async () => {
    if (!cancelPo || !cancelReason.trim()) return;
    setCancelling(true);
    const res = await delhiveryCancelOrder(cancelPo, cancelReason.trim());
    setCancelling(false);
    if (res.ok) {
      message.success("Delhivery booking cancelled");
      setCancelPo(null);
      load(page); // refresh the current view
    } else {
      message.error(res.error || "Could not cancel this Delhivery booking");
    }
  };

  // Every PO on a trip = this row's PO + the others sharing its CRN.
  const tripPos = (row: DelhiveryBookingRow) => [
    row.po_number,
    ...(row.consolidated_with ?? []),
  ];

  // This PO's pickup has already been collected/dispatched → detach is no longer possible (rider has
  // it). "dispatch_pending" is NOT dispatched, so exclude it.
  const isPoCollected = (row: DelhiveryBookingRow) => {
    const s = (row.status ?? "").toLowerCase();
    if (["out_for_delivery", "at_delivery", "order_delivered", "delivered"].includes(s))
      return true;
    const ls = (row.leg_status ?? "").toLowerCase();
    return /picked|dispatched/.test(ls) && !/pending/.test(ls);
  };

  // Action 1 — cancel the WHOLE multistop trip (CRN-level). Frees every PO on it.
  const confirmCancelTrip = (row: DelhiveryBookingRow) => {
    const pos = tripPos(row);
    Modal.confirm({
      title: `Cancel the entire Delhivery trip ${row.order_id ?? ""}?`,
      content: `Cancels the whole delivery and frees all ${pos.length} PO${
        pos.length === 1 ? "" : "s"
      }: ${pos.join(", ")}.`,
      okText: "Cancel trip",
      okButtonProps: { danger: true },
      cancelText: "Keep trip",
      onOk: async () => {
        const res = await delhiveryMultistopCancel(
          row.order_id ? { order_id: row.order_id } : { booking_id: row.booking_id ?? undefined }
        );
        if (res.ok) {
          message.success(
            res.data?.status === "cancellation_requested"
              ? "Trip cancellation requested"
              : "Trip cancelled — all POs freed"
          );
          load(page);
        } else {
          message.error(
            res.error ||
              (res.status === 409
                ? "The rider has already started collecting — use reattempt delivery instead."
                : "Could not cancel this trip.")
          );
          // Re-throw so the confirm dialog stays open on failure.
          return Promise.reject(res.error);
        }
      },
    });
  };

  // Action 2 — decouple just THIS PO from the trip (DB-only). The rider keeps the rest of the run.
  const confirmDetach = (row: DelhiveryBookingRow) => {
    Modal.confirm({
      title: `Remove ${row.po_number} from trip ${row.order_id ?? ""}?`,
      content: `Nothing is sent to Delhivery; the rider still services the other pickups. ${row.po_number} becomes rebookable.`,
      okText: "Remove pickup",
      okButtonProps: { danger: true },
      cancelText: "Keep on trip",
      onOk: async () => {
        const res = await delhiveryMultistopDetach(row.po_number);
        if (res.ok) {
          message.success(`${row.po_number} removed from the trip — now rebookable`);
          load(page);
        } else {
          message.error(
            res.error ||
              (res.status === 409
                ? "This PO can't be removed (already collected, or it's the last one — cancel the trip instead)."
                : "Could not remove this PO.")
          );
          return Promise.reject(res.error);
        }
      },
    });
  };

  const columns: ColumnsType<DelhiveryBookingRow> = [
    {
      title: "Lead ID",
      dataIndex: "lead_id",
      width: 200,
      render: (v: string | null, row) => {
        const parts = [
          typeof row.cargo_weight_kg === "number" && row.cargo_weight_kg > 0
            ? `${row.cargo_weight_kg} kg`
            : null,
          typeof row.cargo_max_length_ft === "number" && row.cargo_max_length_ft > 0
            ? `${row.cargo_max_length_ft} ft`
            : null,
        ].filter(Boolean);
        return (
          <div>
            {/* Lead ID (links to the OMS order) + copy. */}
            {v ? (
              <Space size={4}>
                {row.oms_sales_order_id ? (
                  <a
                    href={`/oms/${row.oms_sales_order_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {v}
                  </a>
                ) : (
                  <span>{v}</span>
                )}
                <Typography.Text copyable={{ text: v }} />
              </Space>
            ) : (
              <span style={{ color: "#bbb" }}>—</span>
            )}
            {/* PO number below the lead id, with the greyed cargo summary under it. */}
            <div style={{ marginTop: 4 }}>
              <Space size={4} wrap>
                <span style={{ fontWeight: 600 }}>{row.po_number}</span>
                {row.is_deleted && <Tag color="red">deleted</Tag>}
                {row.is_revised && <Tag color="orange">revised</Tag>}
              </Space>
              {parts.length > 0 && (
                <div style={{ fontSize: 11, color: "#8c8c8c", whiteSpace: "nowrap" }}>
                  {parts.join(" · ")}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Vendor",
      dataIndex: "vendor_name",
      width: 130,
      render: (v) =>
        v ? (
          <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{v}</span>
        ) : (
          "—"
        ),
    },
    { title: "Order ID", dataIndex: "order_id", render: (v) => v || "—" },
    {
      title: "Delhivery Status",
      dataIndex: "status",
      render: (v: string | null) =>
        v ? <Tag color={statusColor(v)}>{v}</Tag> : "—",
    },
    {
      title: "Items",
      dataIndex: "cargo_items",
      width: 300,
      render: (items: DelhiveryBookingRow["cargo_items"], row) => (
        <CargoItemsCell
          items={items}
          weightKg={row.cargo_weight_kg}
          maxLengthFt={row.cargo_max_length_ft}
        />
      ),
    },
    {
      title: "Rider Info",
      key: "rider_info",
      width: 160,
      render: (_, row) => {
        const { delivery_person_name, delivery_person_contact, vehicle_number, requested_vehicle } = row;
        if (!delivery_person_name && !delivery_person_contact && !vehicle_number && !requested_vehicle)
          return "—";
        return (
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            <div>{delivery_person_name || "—"}</div>
            <div style={{ color: "#8c8c8c" }}>{delivery_person_contact || "—"}</div>
            <div style={{ color: "#8c8c8c" }}>{vehicle_number || "—"}</div>
            {requested_vehicle && (
              <div style={{ color: "#8c8c8c" }}>{requested_vehicle}</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Tracking",
      dataIndex: "tracking_url",
      render: (v: string | null) =>
        v ? (
          <a href={v} target="_blank" rel="noopener noreferrer">
            Track ↗
          </a>
        ) : (
          "—"
        ),
    },
    {
      title: "Route",
      key: "route",
      width: 200,
      render: (_, row) => <RouteCell row={row} />,
    },
    { title: "Pickup", dataIndex: "pickup_time", render: (v) => fmtTime(v) },
    { title: "Delivery", dataIndex: "delivery_time", render: (v) => fmtTime(v) },
    {
      title: "Details",
      key: "details",
      width: 150,
      render: (_: unknown, row) => (
        <Button size="small" onClick={() => setDetailsRow(row)}>
          Additional details
        </Button>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 130,
      render: (_: unknown, row) => {
        // Multistop trip → two cancel-style actions in one menu: whole-trip cancel (CRN-level, gated
        // by is_cancellable) and per-PO decouple (gated by is_multistop_delhivery_booked, hidden once
        // this PO is collected). Non-multistop rows keep the plain reason-based Cancel.
        if (row.is_multistop_delhivery_booked) {
          const canDetach = !row.is_deleted && !isPoCollected(row);
          const items: MenuProps["items"] = [];
          if (canDetach)
            items.push({
              key: "detach",
              label: "Material not ready — remove pickup",
              onClick: () => confirmDetach(row),
            });
          if (row.is_cancellable)
            items.push({
              key: "cancel-trip",
              danger: true,
              label: "Cancel entire trip",
              onClick: () => confirmCancelTrip(row),
            });
          if (items.length === 0) return null;
          return (
            <Dropdown menu={{ items }} trigger={["click"]}>
              <Button size="small" danger>
                Cancel <DownOutlined />
              </Button>
            </Dropdown>
          );
        }
        // Single/legacy booking — plain cancel with a reason (unchanged).
        return row.is_cancellable ? (
          <Button
            size="small"
            danger
            onClick={() => {
              setCancelReason("");
              setCancelPo(row.po_number);
            }}
          >
            Cancel
          </Button>
        ) : null;
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Title level={3}>Delhivery Bookings</Title>
        <Space>
          {/* Tracks every active booking against Delhivery to catch stuck statuses up. Slow; the
              loading guard blocks concurrent / double runs. */}
          <Tooltip title="Check Delhivery for any delivered/cancelled orders whose update didn't come through automatically">
            <Button
              icon={<SyncOutlined />}
              loading={syncing}
              disabled={syncing}
              onClick={onSync}
            >
              Sync delhivery statuses
            </Button>
          </Tooltip>
          {/* Re-runs the current view — same tab, filters and page. */}
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() => load(page)}
            >
              Refresh
            </Button>
          </Tooltip>
        </Space>
      </div>

      {syncResult && (
        <Alert
          style={{ marginBottom: 16 }}
          type={syncResult.errors.length ? "warning" : "success"}
          showIcon
          closable
          onClose={() => setSyncResult(null)}
          message={syncSummary(syncResult)}
          description={
            syncResult.errors.length > 0 ? (
              <Collapse
                ghost
                size="small"
                items={[
                  {
                    key: "errors",
                    label: `Show ${syncResult.errors.length} error${
                      syncResult.errors.length === 1 ? "" : "s"
                    }`,
                    children: (
                      <div style={{ maxHeight: 220, overflowY: "auto" }}>
                        {syncResult.errors.map((e) => (
                          <div key={e.booking_id} style={{ fontSize: 12, marginBottom: 4 }}>
                            <strong>{e.order_id}</strong> — {e.error}
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            ) : undefined
          }
        />
      )}

      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as BookingsTab)}
        items={[
          { key: "active", label: "Active" },
          { key: "delivered", label: "Delivered" },
          { key: "all", label: "All" },
        ]}
      />

      <Card style={{ marginBottom: 16 }}>
        <Space wrap size={12} align="end">
          <div>
            <div style={{ fontSize: 12, color: "#888" }}>PO number</div>
            <Input
              style={{ width: 200 }}
              placeholder="Exact PO number"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              onPressEnter={onSearch}
              allowClear
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#888" }}>Booked between</div>
            <RangePicker
              value={bookedRange as any}
              onChange={(v) => setBookedRange(v as any)}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#888" }}>Order ID</div>
            <Input
              style={{ width: 200 }}
              placeholder="CRN No."
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              onPressEnter={onSearch}
              allowClear
            />
          </div>
          {/* Delhivery status — only on the All tab (Active/Delivered are fixed presets). */}
          {tab === "all" && (
            <div>
              <div style={{ fontSize: 12, color: "#888" }}>Delhivery status</div>
              <Select
                style={{ width: 190 }}
                placeholder="Any status"
                value={status}
                onChange={setStatus}
                allowClear
                options={STATUS_OPTIONS.map((s) => ({ label: s, value: s }))}
              />
            </div>
          )}
          <Button type="primary" loading={loading} onClick={onSearch}>
            Search
          </Button>
          <Button onClick={onReset}>Reset</Button>
        </Space>
      </Card>

      <Table
        rowKey={(r) => `${r.po_number}-${r.order_id ?? ""}-${r.booked_at ?? ""}`}
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: "max-content" }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: count,
          showSizeChanger: false,
          showTotal: (t) => `${t} booking${t === 1 ? "" : "s"}`,
          onChange: (p) => load(p),
        }}
      />

      <Modal
        title={`Cancel Delhivery booking · ${cancelPo ?? ""}`}
        open={!!cancelPo}
        onCancel={() => setCancelPo(null)}
        okText="Cancel booking"
        okButtonProps={{ danger: true, disabled: !cancelReason.trim(), loading: cancelling }}
        cancelText="Keep booking"
        onOk={runCancel}
        destroyOnClose
      >
        <div style={{ marginBottom: 8, color: "#8c8c8c", fontSize: 13 }}>
          Pick a reason or type your own — it's required and sent to Delhivery.
        </div>
        <AutoComplete
          style={{ width: "100%" }}
          value={cancelReason}
          onChange={setCancelReason}
          placeholder="Reason for cancellation"
          options={DELHIVERY_CANCEL_REASONS.map((r) => ({ value: r }))}
          filterOption={(input, option) =>
            (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      </Modal>

      <Drawer
        title={`Additional details · ${detailsRow?.po_number ?? ""}`}
        placement="right"
        width={420}
        open={!!detailsRow}
        onClose={() => setDetailsRow(null)}
        destroyOnClose
      >
        {detailsRow && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Leg status">
              {detailsRow.leg_status ? (
                <Tag>{detailsRow.leg_status}</Tag>
              ) : (
                "—"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="PO created">
              {fmtTime(detailsRow.po_created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Booked at">
              {fmtTime(detailsRow.booked_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Booked by">
              {detailsRow.booked_by || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Pickup contact">
              {contactDisplay(detailsRow.pickup_contact)}
            </Descriptions.Item>
            <Descriptions.Item label="Drop contact">
              {contactDisplay(detailsRow.drop_contact)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default DelhiveryBookings;
